import {
    Globe2,
    UtensilsCrossed,
    Users,
    HandHeart,
    Briefcase,
    ShieldCheck,
} from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/primitives";
import { CTABand } from "../components/CTABand";
import { LOGO_MARK } from "../lib/site";

const PILLARS = [
    {
        icon: Globe2,
        num: "01",
        name: "Environment",
        desc: "Energy, water, emissions, biodiversity and environmental stewardship.",
        span: "md:col-span-2",
    },
    {
        icon: UtensilsCrossed,
        num: "02",
        name: "Food",
        desc: "Food-waste prevention, sustainable food practices, responsible sourcing and food recovery.",
        span: "md:col-span-1",
    },
    {
        icon: Users,
        num: "03",
        name: "People",
        desc: "Employee participation, training, wellbeing, inclusion and professional development.",
        span: "md:col-span-1",
    },
    {
        icon: HandHeart,
        num: "04",
        name: "Community",
        desc: "Local sourcing, community partnerships, local economic value and responsible tourism.",
        span: "md:col-span-1",
    },
    {
        icon: Briefcase,
        num: "05",
        name: "Business",
        desc: "Operational efficiency, cost savings, innovation, resilience and long-term financial sustainability.",
        span: "md:col-span-1",
    },
    {
        icon: ShieldCheck,
        num: "06",
        name: "Governance",
        desc: "Measurement, accountability, transparency, policies and continuous improvement.",
        span: "md:col-span-2",
    },
];

const Framework = () => (
    <main>
        <SEO
            title="The GGH Sustainable Hospitality Framework | Global Green Hotel"
            description="Six connected pillars — Environment, Food, People, Community, Business and Governance — guiding practical, measurable hotel sustainability."
        />
        <PageHero
            testid="framework-hero"
            eyebrow="Our Framework"
            titleLines={["The GGH Sustainable", "Hospitality Framework"]}
            description="Six connected pillars that help hotels look at sustainability as a whole system — environmental, human and financial."
        />

        {/* Beyond Sustainability — Regenerative Hospitality */}
        <section data-testid="framework-regenerative" className="grain relative overflow-hidden bg-[#07211A] py-24 sm:py-32">
            <div className="relative z-[2] mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <Eyebrow dark>Beyond Sustainability</Eyebrow>
                            <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#FDFBF7] sm:text-4xl">
                                Regenerative Hospitality
                            </h2>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-7 text-base leading-relaxed text-[#FDFBF7]/75 md:text-lg">
                                At Global Green Hotel (GGH), we believe responsible hospitality
                                should go beyond reducing negative impacts. Regenerative
                                hospitality means creating positive and lasting value for the
                                environment, people, communities, destinations, and the hospitality
                                businesses that depend on them.
                            </p>
                            <p className="mt-5 text-base leading-relaxed text-[#FDFBF7]/75 md:text-lg">
                                For GGH, regeneration is about helping hotels move from simply
                                using fewer resources toward actively contributing to healthier
                                places and stronger communities. This can include reducing food
                                waste, supporting local producers and businesses, protecting
                                natural resources, respecting local culture, investing in
                                employees, strengthening community relationships, and making
                                responsible decisions that support long-term business resilience.
                            </p>
                            <p className="mt-5 text-base leading-relaxed text-[#FDFBF7]/75 md:text-lg">
                                Our six sustainability pillars — Environment, Food, People,
                                Community, Business, and Governance — provide the foundation for
                                this approach.
                            </p>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-5">
                        <Reveal delay={0.25}>
                            <div className="rounded-2xl border border-[#C2A160]/25 bg-[#04150D]/60 p-8">
                                <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-[#C2A160]">
                                    The Journey Is Continuous
                                </p>
                                <ol className="mt-6 space-y-4" data-testid="regenerative-journey">
                                    {["Measure", "Understand", "Improve", "Regenerate", "Create Lasting Impact"].map((step, i) => (
                                        <li key={step} className="flex items-center gap-4">
                                            <span className="font-display w-8 text-xl font-medium text-[#C2A160]/60">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <span className="flex-1 rounded-full border border-[#C2A160]/30 bg-[#C2A160]/10 px-4 py-2 text-sm font-bold text-[#FDFBF7]">
                                                {step}
                                            </span>
                                        </li>
                                    ))}
                                </ol>
                                <p className="font-display mt-8 border-t border-white/10 pt-6 text-lg italic leading-relaxed text-[#D9B96A]">
                                    “GGH does not view regenerative hospitality as a single
                                    achievement or label. It is an ongoing commitment to making
                                    hospitality better for people, planet, communities, and
                                    business.”
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>

        <section data-testid="framework-pillars" className="py-24 sm:py-32">
            <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <Reveal>
                    <Eyebrow>The Six Pillars</Eyebrow>
                    <h2 className="font-display mt-4 max-w-3xl text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                        Designed to work together, not in isolation
                    </h2>
                </Reveal>

                <div className="mt-14 grid gap-4 md:grid-cols-3" data-testid="pillars-bento">
                    {PILLARS.map(({ icon: Icon, num, name, desc, span }, i) => (
                        <Reveal key={name} delay={0.06 * i} y={24} className={span}>
                            <article
                                data-testid={`pillar-${name.toLowerCase()}`}
                                className="card-lift group flex h-full flex-col justify-between rounded-2xl border border-[#E8E6E1] bg-white p-8 transition-colors duration-300 hover:border-[#1A3B2B]"
                            >
                                <div className="flex items-start justify-between">
                                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1A3B2B] text-[#D9B96A] transition-colors duration-300 group-hover:bg-[#274E3A]">
                                        <Icon size={24} aria-hidden="true" />
                                    </span>
                                    <span aria-hidden="true" className="font-display text-3xl font-medium text-[#1A3B2B]/12">
                                        {num}
                                    </span>
                                </div>
                                <div className="mt-10">
                                    <h3 className="font-display text-2xl font-medium uppercase tracking-wide text-[#0A1711]">
                                        {name}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-[#4A5D54]">{desc}</p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        {/* How the pillars connect */}
        <section data-testid="framework-diagram" className="grain relative overflow-hidden bg-[#07211A] py-24 sm:py-32">
            <div className="relative z-[2] mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <Reveal>
                    <Eyebrow dark>One System</Eyebrow>
                    <h2 className="font-display mt-4 max-w-3xl text-3xl font-medium tracking-tight text-[#FDFBF7] sm:text-4xl">
                        Six pillars supporting one goal
                    </h2>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#FDFBF7]/70 md:text-lg">
                        Each pillar reinforces the others. Progress in food reduces cost and
                        emissions. Engaged people make operations stick. Governance turns effort
                        into credible, measurable performance.
                    </p>
                </Reveal>

                <div className="mt-16" data-testid="pillars-diagram">
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                        {PILLARS.map(({ icon: Icon, name }, i) => (
                            <Reveal key={name} delay={0.08 * i} y={26}>
                                <div className="flex flex-col items-center rounded-2xl border border-[#C2A160]/25 bg-[#04150D]/60 p-6 text-center">
                                    <Icon size={26} className="text-[#D9B96A]" aria-hidden="true" />
                                    <span className="mt-4 text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]">
                                        {name}
                                    </span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.5}>
                        <div className="mt-3 flex items-center justify-center gap-4 rounded-2xl bg-[#C2A160] px-6 py-5">
                            <img src={LOGO_MARK} alt="" aria-hidden="true" className="h-8 w-auto" />
                            <p className="font-display text-lg font-semibold tracking-tight text-[#0A1711] sm:text-xl">
                                Measurable, credible sustainability performance
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>

        <CTABand
            testid="cta-band-framework"
            heading="See How the Framework Applies to Your Hotel"
            text="GGH is developing practical ways for hotels to use this framework to understand and improve their sustainability performance."
            primary={{ to: "/for-hotels", label: "Explore For Hotels" }}
            secondary={{ to: "/contact", label: "Talk to GGH" }}
        />
    </main>
);

export default Framework;
